import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { notifyGlobal } from '@/context/NotificationContext';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  Legend,
} from 'recharts';
import {
  TrendingUp,
  IndianRupee,
  ShieldCheck,
  Truck,
  CheckCircle2,
  Clock,
  Download,
  RefreshCw,
  FileSpreadsheet,
  Search,
  Layers,
  Award,
  X,
  Building2,
  Calendar,
} from 'lucide-react';
import { formatInr } from '@/lib/utils';

// Types
type AnalyticsTab =
  | 'SPEND'
  | 'VENDOR_OTIF'
  | 'SAVINGS_PPV'
  | 'CONTRACT_MAVERICK'
  | 'WORKING_CAPITAL';

interface CategorySpendItem {
  category: string;
  commodityCode: string;
  supplier: string;
  vendorCode: string;
  plant: string;
  spend: number;
  budget: number;
  savings: number;
  ppvPercent: number;
  ppvStatus: 'FAVORABLE' | 'UNFAVORABLE';
  otifPercent: number;
  compliance: 'COMPLIANT' | 'CONTRACT_AUDIT' | 'MAVERICK_ALERT';
}

export const AnalyticsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<AnalyticsTab>('SPEND');
  const [selectedPeriod, setSelectedPeriod] = useState('FY_2025_26');
  const [selectedPlant, setSelectedPlant] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Report Modal State
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [reportType, setReportType] = useState('SPEND_PPV');
  const [reportFormat, setReportFormat] = useState('PDF');
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [reportSuccessMessage, setReportSuccessMessage] = useState<string | null>(null);

  const handleRefresh = () => {
    setIsRefreshing(true);
    setTimeout(() => {
      setIsRefreshing(false);
    }, 600);
  };

  // Mock Executive Data in Indian Rupees (₹)
  const monthlySpendTrend = [
    { month: 'Apr 25', spend: 38500000, savings: 3200000, budget: 40000000 },
    { month: 'May 25', spend: 41200000, savings: 3600000, budget: 42000000 },
    { month: 'Jun 25', spend: 39800000, savings: 3400000, budget: 41000000 },
    { month: 'Jul 25', spend: 44500000, savings: 4100000, budget: 45000000 },
    { month: 'Aug 25', spend: 42300000, savings: 3900000, budget: 43000000 },
    { month: 'Sep 25', spend: 46800000, savings: 4500000, budget: 47000000 },
    { month: 'Oct 25', spend: 48900000, savings: 4800000, budget: 49000000 },
    { month: 'Nov 25', spend: 45200000, savings: 4200000, budget: 46000000 },
    { month: 'Dec 25', spend: 51200000, savings: 5200000, budget: 52000000 },
    { month: 'Jan 26', spend: 47600000, savings: 4600000, budget: 48000000 },
    { month: 'Feb 26', spend: 49800000, savings: 4900000, budget: 50000000 },
    { month: 'Mar 26', spend: 53100000, savings: 5600000, budget: 54000000 },
  ];

  const categoryDistribution = [
    { category: 'Heavy Machinery & Capex', spend: 142500000, color: '#2563eb' },
    { category: 'Steel & Raw Metallurgy', spend: 118400000, color: '#059669' },
    { category: 'Electrical & Automation', spend: 84600000, color: '#7c3aed' },
    { category: 'Piping, Valves & Hydraulics', spend: 62800000, color: '#d97706' },
    { category: 'MRO & Plant Consumables', spend: 45200000, color: '#0284c7' },
    { category: 'Industrial Logistics & Freight', spend: 33040000, color: '#e11d48' },
  ];

  const vendorPerformanceData = [
    { vendor: 'Tata Steel Ltd', spend: 88500000, otif: 97.4, qualityPpm: 120 },
    { vendor: 'L&T Heavy Engineering', spend: 76200000, otif: 96.1, qualityPpm: 210 },
    { vendor: 'Siemens Industrial India', spend: 54800000, otif: 94.8, qualityPpm: 95 },
    { vendor: 'Kirloskar Brothers Ltd', spend: 41300000, otif: 93.2, qualityPpm: 340 },
    { vendor: 'Thermax Energy Solutions', spend: 36700000, otif: 95.5, qualityPpm: 180 },
    { vendor: 'ABB India Ltd', spend: 32100000, otif: 98.2, qualityPpm: 80 },
  ];

  const savingsByQuarter = [
    { quarter: 'Q1 FY25-26', target: 9500000, realized: 10200000, ppvFavorable: 2800000 },
    { quarter: 'Q2 FY25-26', target: 11000000, realized: 12500000, ppvFavorable: 3400000 },
    { quarter: 'Q3 FY25-26', target: 12500000, realized: 14200000, ppvFavorable: 4100000 },
    { quarter: 'Q4 FY25-26', target: 14000000, realized: 15400000, ppvFavorable: 4600000 },
  ];

  const contractComplianceShare = [
    { name: 'On-Catalog Master Agreements', value: 91.2, color: '#059669' },
    { name: 'Maverick Off-Catalog Leakage', value: 8.8, color: '#e11d48' },
  ];

  const apAgingBuckets = [
    { bucket: 'Current (0-30 Days)', amount: 284000000, color: '#059669' },
    { bucket: '31-60 Days (Grace)', amount: 98500000, color: '#2563eb' },
    { bucket: '61-90 Days (Overdue)', amount: 34200000, color: '#d97706' },
    { bucket: '90+ Days (Critical Hold)', amount: 12800000, color: '#e11d48' },
  ];

  // Comprehensive Table Records
  const reportRecords: CategorySpendItem[] = [
    {
      category: 'Structural Carbon Steel Plates',
      commodityCode: 'UNSPSC-301017',
      supplier: 'Tata Steel Ltd',
      vendorCode: 'VND-1001',
      plant: 'Plant 1010 (Mumbai)',
      spend: 48500000,
      budget: 52000000,
      savings: 3500000,
      ppvPercent: -6.7,
      ppvStatus: 'FAVORABLE',
      otifPercent: 97.4,
      compliance: 'COMPLIANT',
    },
    {
      category: 'Pressure Vessels & Heat Exchangers',
      commodityCode: 'UNSPSC-401018',
      supplier: 'L&T Heavy Engineering',
      vendorCode: 'VND-1002',
      plant: 'Plant 1020 (Pune)',
      spend: 42800000,
      budget: 45000000,
      savings: 2200000,
      ppvPercent: -4.8,
      ppvStatus: 'FAVORABLE',
      otifPercent: 96.1,
      compliance: 'COMPLIANT',
    },
    {
      category: 'PLC Automation Panels (Simatic S7)',
      commodityCode: 'UNSPSC-321517',
      supplier: 'Siemens Industrial India',
      vendorCode: 'VND-1003',
      plant: 'Plant 1030 (Chennai)',
      spend: 34200000,
      budget: 35000000,
      savings: 800000,
      ppvPercent: -2.3,
      ppvStatus: 'FAVORABLE',
      otifPercent: 94.8,
      compliance: 'COMPLIANT',
    },
    {
      category: 'Heavy Multistage Centrifugal Pumps',
      commodityCode: 'UNSPSC-401515',
      supplier: 'Kirloskar Brothers Ltd',
      vendorCode: 'VND-1004',
      plant: 'Plant 1010 (Mumbai)',
      spend: 28900000,
      budget: 27500000,
      savings: -1400000,
      ppvPercent: 5.1,
      ppvStatus: 'UNFAVORABLE',
      otifPercent: 91.2,
      compliance: 'CONTRACT_AUDIT',
    },
    {
      category: 'Biomass Steam Boiler Systems',
      commodityCode: 'UNSPSC-401020',
      supplier: 'Thermax Energy Solutions',
      vendorCode: 'VND-1005',
      plant: 'Plant 1020 (Pune)',
      spend: 26400000,
      budget: 28000000,
      savings: 1600000,
      ppvPercent: -5.7,
      ppvStatus: 'FAVORABLE',
      otifPercent: 95.5,
      compliance: 'COMPLIANT',
    },
    {
      category: 'Medium Voltage Switchgear 11kV',
      commodityCode: 'UNSPSC-391215',
      supplier: 'ABB India Ltd',
      vendorCode: 'VND-1006',
      plant: 'Plant 1030 (Chennai)',
      spend: 22100000,
      budget: 23500000,
      savings: 1400000,
      ppvPercent: -6.0,
      ppvStatus: 'FAVORABLE',
      otifPercent: 98.2,
      compliance: 'COMPLIANT',
    },
    {
      category: 'Unplanned Workshop Emergency Valves',
      commodityCode: 'UNSPSC-401416',
      supplier: 'Ad-hoc Spot Vendors',
      vendorCode: 'VND-SPOT-99',
      plant: 'Plant 1040 (NCR)',
      spend: 11400000,
      budget: 8500000,
      savings: -2900000,
      ppvPercent: 34.1,
      ppvStatus: 'UNFAVORABLE',
      otifPercent: 82.5,
      compliance: 'MAVERICK_ALERT',
    },
  ];

  const filteredRecords = useMemo(() => {
    if (!searchTerm) return reportRecords;
    const q = searchTerm.toLowerCase();
    return reportRecords.filter(
      (r) =>
        r.category.toLowerCase().includes(q) ||
        r.supplier.toLowerCase().includes(q) ||
        r.vendorCode.toLowerCase().includes(q) ||
        r.commodityCode.toLowerCase().includes(q) ||
        r.plant.toLowerCase().includes(q)
    );
  }, [searchTerm, reportRecords]);

  const handleExportCsv = () => {
    const headers = [
      'Category',
      'UNSPSC Code',
      'Primary Supplier',
      'Vendor Code',
      'Operating Plant',
      'Invoiced Spend (INR)',
      'Budget Allocation (INR)',
      'Realized Savings (INR)',
      'PPV Variance %',
      'PPV Status',
      'OTIF Performance %',
      'Compliance State',
    ];
    const rows = filteredRecords.map((r) => [
      `"${r.category}"`,
      r.commodityCode,
      `"${r.supplier}"`,
      r.vendorCode,
      `"${r.plant}"`,
      r.spend,
      r.budget,
      r.savings,
      `${r.ppvPercent}%`,
      r.ppvStatus,
      `${r.otifPercent}%`,
      r.compliance,
    ]);

    const csvContent =
      'data:text/csv;charset=utf-8,' +
      [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `ProcureAI_Spend_Analytics_${selectedPeriod}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSimulateReportGeneration = (e: React.FormEvent) => {
    e.preventDefault();
    setIsGeneratingReport(true);
    setTimeout(() => {
      // Trigger actual document download
      const filename = `ProcureAI_${reportType}_${selectedPeriod}.${reportFormat === 'CSV' ? 'csv' : reportFormat === 'EXCEL' ? 'csv' : 'txt'}`;
      const reportHeaders = ['Category', 'UNSPSC Code', 'Supplier', 'Vendor Code', 'Plant', 'Spend (INR)', 'Budget (INR)', 'Savings (INR)', 'PPV %', 'OTIF %', 'Compliance'];
      const reportRows = filteredRecords.map((r) => [
        `"${r.category}"`,
        r.commodityCode,
        `"${r.supplier}"`,
        r.vendorCode,
        `"${r.plant}"`,
        r.spend,
        r.budget,
        r.savings,
        `${r.ppvPercent}%`,
        `${r.otifPercent}%`,
        r.compliance,
      ]);
      const content = `PROCUREAI EXECUTIVE INTELLIGENCE REPORT\nReport Type: ${reportType}\nPeriod: ${selectedPeriod}\nGenerated: ${new Date().toISOString()}\n\n` +
        [reportHeaders.join(','), ...reportRows.map((e) => e.join(','))].join('\n');
      const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setIsGeneratingReport(false);
      setReportSuccessMessage(`Report "${filename}" compiled and downloaded successfully.`);
      notifyGlobal(
        'Executive Report Generated',
        `Dossier "${filename}" compiled and downloaded.`,
        'StatusChange'
      );
      setTimeout(() => {
        setIsReportModalOpen(false);
        setReportSuccessMessage(null);
      }, 1200);
    }, 1200);
  };

  return (
    <div className="space-y-6 pb-20 font-poppins">
      {/* Top Executive Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-border/60 pb-4">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-xl bg-primary/10 text-primary">
              <TrendingUp className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl font-extrabold tracking-tight text-foreground font-poppins">
                Procurement Spend Analytics & Executive Reports
              </h1>
              <p className="text-xs text-foreground-secondary font-medium mt-0.5 font-poppins">
                Enterprise spend intelligence, Purchase Price Variance (PPV), supplier OTIF benchmarks, contract compliance, and cash outflow forecasting.
              </p>
            </div>
          </div>
        </div>

        {/* Global Selectors & Action Buttons */}
        <div className="flex flex-wrap items-center gap-2.5">
          {/* Period Selector */}
          <div className="flex items-center gap-1.5 bg-card border border-border rounded-xl px-2.5 py-1.5 shadow-2xs">
            <Calendar className="h-3.5 w-3.5 text-primary" />
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="bg-transparent text-xs font-bold text-foreground focus:outline-none cursor-pointer font-poppins"
            >
              <option value="FY_2025_26">Fiscal Year 2025-26</option>
              <option value="Q4_2026">Q4 (Jan - Mar 2026)</option>
              <option value="Q3_2025">Q3 (Oct - Dec 2025)</option>
              <option value="YTD">Year-to-Date (YTD)</option>
              <option value="LAST_30_DAYS">Last 30 Days</option>
            </select>
          </div>

          {/* Plant Selector */}
          <div className="flex items-center gap-1.5 bg-card border border-border rounded-xl px-2.5 py-1.5 shadow-2xs">
            <Building2 className="h-3.5 w-3.5 text-primary" />
            <select
              value={selectedPlant}
              onChange={(e) => setSelectedPlant(e.target.value)}
              className="bg-transparent text-xs font-bold text-foreground focus:outline-none cursor-pointer font-poppins"
            >
              <option value="ALL">All Plants (Pan-India)</option>
              <option value="1010">Plant 1010 — Mumbai Central</option>
              <option value="1020">Plant 1020 — Pune Heavy Plant</option>
              <option value="1030">Plant 1030 — Chennai Hub</option>
              <option value="1040">Plant 1040 — NCR Facility</option>
            </select>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            className="rounded-xl font-bold font-poppins cursor-pointer"
          >
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${isRefreshing ? 'animate-spin' : ''}`} /> Refresh
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={handleExportCsv}
            className="rounded-xl font-bold font-poppins cursor-pointer border-border"
          >
            <Download className="h-3.5 w-3.5 mr-1.5" /> Export CSV
          </Button>

          <Button
            size="sm"
            onClick={() => setIsReportModalOpen(true)}
            className="rounded-xl font-bold bg-primary hover:bg-primary/90 text-primary-foreground font-poppins cursor-pointer shadow-xs"
          >
            <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" /> Generate Report
          </Button>
        </div>
      </div>

      {/* 6 Executive KPI Summary Cards (INR ₹) */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3.5 font-poppins">
        {/* Card 1: Total Spend */}
        <Card className="rounded-2xl border-border bg-card shadow-2xs">
          <CardContent className="p-4 space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-extrabold tracking-wider uppercase text-foreground-secondary">
                Total Spend (INR)
              </span>
              <div className="p-1.5 rounded-lg bg-blue-500/10 text-blue-600">
                <IndianRupee className="h-4 w-4" />
              </div>
            </div>
            <div className="text-xl font-black text-foreground font-mono">
              ₹48.65 Cr
            </div>
            <p className="text-[10px] font-bold text-emerald-600">+4.2% YoY vs FY24-25</p>
          </CardContent>
        </Card>

        {/* Card 2: Cost Savings */}
        <Card className="rounded-2xl border-border bg-card shadow-2xs">
          <CardContent className="p-4 space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-extrabold tracking-wider uppercase text-emerald-600">
                Cost Savings
              </span>
              <div className="p-1.5 rounded-lg bg-emerald-500/10 text-emerald-600">
                <ShieldCheck className="h-4 w-4" />
              </div>
            </div>
            <div className="text-xl font-black text-emerald-600 font-mono">
              ₹4.12 Cr
            </div>
            <p className="text-[10px] font-bold text-emerald-600/90">8.5% realized margin</p>
          </CardContent>
        </Card>

        {/* Card 3: PPV Variance */}
        <Card className="rounded-2xl border-border bg-card shadow-2xs">
          <CardContent className="p-4 space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-extrabold tracking-wider uppercase text-purple-600">
                PPV Variance
              </span>
              <div className="p-1.5 rounded-lg bg-purple-500/10 text-purple-600">
                <Award className="h-4 w-4" />
              </div>
            </div>
            <div className="text-xl font-black text-purple-700 font-mono">
              -₹1.35 Cr
            </div>
            <p className="text-[10px] font-bold text-purple-600/90">2.8% favorable to budget</p>
          </CardContent>
        </Card>

        {/* Card 4: Supplier OTIF */}
        <Card className="rounded-2xl border-border bg-card shadow-2xs">
          <CardContent className="p-4 space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-extrabold tracking-wider uppercase text-blue-600">
                Supplier OTIF
              </span>
              <div className="p-1.5 rounded-lg bg-blue-500/10 text-blue-600">
                <Truck className="h-4 w-4" />
              </div>
            </div>
            <div className="text-xl font-black text-blue-700 font-mono">
              94.6%
            </div>
            <p className="text-[10px] font-bold text-foreground-muted">1,420 of 1,501 on-time</p>
          </CardContent>
        </Card>

        {/* Card 5: Contract Compliance */}
        <Card className="rounded-2xl border-border bg-card shadow-2xs">
          <CardContent className="p-4 space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-extrabold tracking-wider uppercase text-emerald-600">
                Contract On-Catalog
              </span>
              <div className="p-1.5 rounded-lg bg-emerald-500/10 text-emerald-600">
                <CheckCircle2 className="h-4 w-4" />
              </div>
            </div>
            <div className="text-xl font-black text-emerald-600 font-mono">
              91.2%
            </div>
            <p className="text-[10px] font-bold text-foreground-muted">Maverick spend &lt; 8.8%</p>
          </CardContent>
        </Card>

        {/* Card 6: DPO Days */}
        <Card className="rounded-2xl border-border bg-card shadow-2xs">
          <CardContent className="p-4 space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-extrabold tracking-wider uppercase text-foreground-secondary">
                DPO / Working Cap
              </span>
              <div className="p-1.5 rounded-lg bg-amber-500/10 text-amber-600">
                <Clock className="h-4 w-4" />
              </div>
            </div>
            <div className="text-xl font-black text-foreground font-mono">
              46.2 Days
            </div>
            <p className="text-[10px] font-bold text-emerald-600">Target: 45.0 Days</p>
          </CardContent>
        </Card>
      </div>

      {/* Segmented Squeezed Intelligence Tabs */}
      <div className="flex border-b border-border overflow-x-auto gap-1.5 text-xs font-poppins pb-1">
        {[
          { key: 'SPEND', label: 'Spend & Category Breakdown', icon: Layers },
          { key: 'VENDOR_OTIF', label: 'Vendor Performance & OTIF', icon: Truck },
          { key: 'SAVINGS_PPV', label: 'Cost Savings & PPV Variance', icon: ShieldCheck },
          { key: 'CONTRACT_MAVERICK', label: 'Contract & Maverick Leakage', icon: CheckCircle2 },
          { key: 'WORKING_CAPITAL', label: 'Working Capital & AP Aging', icon: Clock },
        ].map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as AnalyticsTab)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl font-semibold transition-all whitespace-nowrap cursor-pointer font-poppins ${
                isActive
                  ? 'bg-primary text-primary-foreground font-bold shadow-xs'
                  : 'bg-card border border-border text-foreground-secondary hover:bg-muted hover:text-foreground'
              }`}
            >
              <Icon className="h-4 w-4" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Dynamic Visualizations by Active Tab */}
      {activeTab === 'SPEND' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 font-poppins">
          {/* Chart 1: Monthly Trajectory Area Chart */}
          <Card className="lg:col-span-2 rounded-2xl border-border bg-card shadow-xs">
            <CardHeader className="p-4 border-b border-border/60">
              <CardTitle className="text-sm font-bold text-foreground flex items-center justify-between">
                <span>Monthly Invoiced Spend & Cost Savings Trajectory (INR ₹)</span>
                <span className="text-[11px] text-foreground-muted font-normal">FY 2025-26 Actuals</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 h-80">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={monthlySpendTrend} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="spendGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2563eb" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="savingsGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#059669" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#059669" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    tickFormatter={(val) => `₹${(val / 10000000).toFixed(1)}Cr`}
                  />
                  <Tooltip
                    formatter={(val: number) => [formatInr(val), '']}
                    labelStyle={{ fontWeight: 'bold' }}
                  />
                  <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }} />
                  <Area
                    type="monotone"
                    dataKey="spend"
                    name="Invoiced Spend"
                    stroke="#2563eb"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#spendGrad)"
                  />
                  <Area
                    type="monotone"
                    dataKey="savings"
                    name="Cost Savings"
                    stroke="#059669"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#savingsGrad)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Chart 2: Category Distribution Pie / Donut */}
          <Card className="rounded-2xl border-border bg-card shadow-xs">
            <CardHeader className="p-4 border-b border-border/60">
              <CardTitle className="text-sm font-bold text-foreground">
                Commodity Spend Share (%)
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 h-80 flex flex-col items-center justify-center">
              <ResponsiveContainer width="100%" height={210}>
                <PieChart>
                  <Pie
                    data={categoryDistribution}
                    dataKey="spend"
                    nameKey="category"
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={85}
                    paddingAngle={3}
                  >
                    {categoryDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(val: number) => [formatInr(val), 'Spend']} />
                </PieChart>
              </ResponsiveContainer>
              <div className="w-full grid grid-cols-2 gap-1.5 text-[10px] pt-2 border-t border-border/40">
                {categoryDistribution.slice(0, 4).map((c, i) => (
                  <div key={i} className="flex items-center gap-1.5 truncate">
                    <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: c.color }} />
                    <span className="truncate text-foreground-secondary">{c.category}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'VENDOR_OTIF' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 font-poppins">
          <Card className="rounded-2xl border-border bg-card shadow-xs">
            <CardHeader className="p-4 border-b border-border/60">
              <CardTitle className="text-sm font-bold text-foreground">
                Top Strategic Suppliers by Invoiced Spend (INR ₹)
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={vendorPerformanceData} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                  <XAxis
                    type="number"
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v) => `₹${(v / 10000000).toFixed(0)}Cr`}
                  />
                  <YAxis type="category" dataKey="vendor" tick={{ fontSize: 11 }} width={130} />
                  <Tooltip formatter={(v: number) => [formatInr(v), 'Total Spend']} />
                  <Bar dataKey="spend" fill="#2563eb" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-border bg-card shadow-xs">
            <CardHeader className="p-4 border-b border-border/60">
              <CardTitle className="text-sm font-bold text-foreground">
                Supplier On-Time In-Full (OTIF) Delivery Index (%)
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={vendorPerformanceData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                  <XAxis dataKey="vendor" tick={{ fontSize: 10 }} />
                  <YAxis domain={[80, 100]} tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
                  <Tooltip formatter={(v: number) => [`${v}%`, 'OTIF Delivery']} />
                  <Bar dataKey="otif" fill="#059669" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'SAVINGS_PPV' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 font-poppins">
          <Card className="rounded-2xl border-border bg-card shadow-xs">
            <CardHeader className="p-4 border-b border-border/60">
              <CardTitle className="text-sm font-bold text-foreground">
                Target vs Realized Cost Savings (INR ₹)
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={savingsByQuarter}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                  <XAxis dataKey="quarter" tick={{ fontSize: 11 }} />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v) => `₹${(v / 10000000).toFixed(1)}Cr`}
                  />
                  <Tooltip formatter={(v: number) => [formatInr(v), '']} />
                  <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }} />
                  <Bar dataKey="target" name="Contract Target" fill="#94a3b8" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="realized" name="Realized Savings" fill="#059669" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-border bg-card shadow-xs">
            <CardHeader className="p-4 border-b border-border/60">
              <CardTitle className="text-sm font-bold text-foreground">
                Quarterly Favorable PPV Contribution (INR ₹)
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 h-80">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={savingsByQuarter}>
                  <defs>
                    <linearGradient id="ppvGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#7c3aed" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#7c3aed" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                  <XAxis dataKey="quarter" tick={{ fontSize: 11 }} />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v) => `₹${(v / 100000).toFixed(0)}L`}
                  />
                  <Tooltip formatter={(v: number) => [formatInr(v), 'Favorable PPV']} />
                  <Area
                    type="monotone"
                    dataKey="ppvFavorable"
                    name="Favorable PPV"
                    stroke="#7c3aed"
                    strokeWidth={2}
                    fill="url(#ppvGrad)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'CONTRACT_MAVERICK' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 font-poppins">
          <Card className="rounded-2xl border-border bg-card shadow-xs">
            <CardHeader className="p-4 border-b border-border/60">
              <CardTitle className="text-sm font-bold text-foreground">
                On-Contract vs Maverick Spend
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 h-80 flex flex-col items-center justify-center">
              <ResponsiveContainer width="100%" height={210}>
                <PieChart>
                  <Pie
                    data={contractComplianceShare}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={85}
                    paddingAngle={3}
                  >
                    {contractComplianceShare.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(val: number) => [`${val}%`, 'Share']} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-1 text-xs w-full pt-2 border-t border-border/40 text-center">
                <p className="font-extrabold text-emerald-600">91.2% Spend Under Master Contract</p>
                <p className="text-foreground-muted text-[11px]">8.8% Off-catalog spot leakage detected</p>
              </div>
            </CardContent>
          </Card>

          <Card className="lg:col-span-2 rounded-2xl border-border bg-card shadow-xs">
            <CardHeader className="p-4 border-b border-border/60">
              <CardTitle className="text-sm font-bold text-foreground">
                Category Compliance Matrix
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-3 text-xs">
              <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl flex items-center justify-between">
                <div>
                  <span className="font-bold text-emerald-700 block">Tier-1 Direct Materials: 97.2% Compliant</span>
                  <span className="text-[11px] text-foreground-muted">All raw steel, Capex, and turbines fully routed via MSA catalogs.</span>
                </div>
                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-600 text-white">HEALTHY</span>
              </div>

              <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl flex items-center justify-between">
                <div>
                  <span className="font-bold text-amber-700 block">MRO Consumables & Tools: 86.4% Compliant</span>
                  <span className="text-[11px] text-foreground-muted">Plant 1040 (NCR) identified with recurring non-catalog spot POs.</span>
                </div>
                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-600 text-white">REVIEW</span>
              </div>

              <div className="p-3 bg-rose-500/10 border border-rose-500/20 rounded-xl flex items-center justify-between">
                <div>
                  <span className="font-bold text-rose-700 block">Emergency Valves & Ad-Hoc Fittings: 65.9% Compliant</span>
                  <span className="text-[11px] text-foreground-muted">34.1% premium paid on spot purchases over baseline contract rates.</span>
                </div>
                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-rose-600 text-white">ALERT</span>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'WORKING_CAPITAL' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 font-poppins">
          <Card className="rounded-2xl border-border bg-card shadow-xs">
            <CardHeader className="p-4 border-b border-border/60">
              <CardTitle className="text-sm font-bold text-foreground">
                Accounts Payable Aging Buckets (INR ₹)
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={apAgingBuckets}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                  <XAxis dataKey="bucket" tick={{ fontSize: 10 }} />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v) => `₹${(v / 10000000).toFixed(0)}Cr`}
                  />
                  <Tooltip formatter={(v: number) => [formatInr(v), 'Payables Due']} />
                  <Bar dataKey="amount" fill="#2563eb" radius={[4, 4, 0, 0]}>
                    {apAgingBuckets.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-border bg-card shadow-xs">
            <CardHeader className="p-4 border-b border-border/60">
              <CardTitle className="text-sm font-bold text-foreground">
                Working Capital Optimization Metrics
              </CardTitle>
            </CardHeader>
            <CardContent className="p-5 space-y-4 text-xs">
              <div className="p-3.5 bg-muted/40 rounded-xl border border-border flex justify-between items-center">
                <div>
                  <span className="font-bold text-foreground block">Days Payable Outstanding (DPO)</span>
                  <span className="text-[11px] text-foreground-muted">Extended payment terms capture working capital float.</span>
                </div>
                <span className="text-base font-black text-primary font-mono">46.2 Days</span>
              </div>

              <div className="p-3.5 bg-muted/40 rounded-xl border border-border flex justify-between items-center">
                <div>
                  <span className="font-bold text-foreground block">Early Settlement Cash Discount Capture</span>
                  <span className="text-[11px] text-foreground-muted">2% 10 Net 30 terms captured across Tier-1 vendors.</span>
                </div>
                <span className="text-base font-black text-emerald-600 font-mono">₹48.2 Lakhs</span>
              </div>

              <div className="p-3.5 bg-muted/40 rounded-xl border border-border flex justify-between items-center">
                <div>
                  <span className="font-bold text-foreground block">Unmatched Invoice Hold Value</span>
                  <span className="text-[11px] text-foreground-muted">Protected disbursements held due to 3-way match variances.</span>
                </div>
                <span className="text-base font-black text-amber-600 font-mono">₹1.28 Cr</span>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Audited Intelligence Report Ledger Table */}
      <Card className="border-border rounded-2xl shadow-xs overflow-hidden font-poppins">
        <CardHeader className="p-4 border-b border-border/60 bg-muted/20">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-bold text-foreground">
                Detailed Spend & Performance Audit Ledger
              </h3>
              <p className="text-xs text-foreground-secondary">
                Line-by-line categorization, budget variances, realized savings, and OTIF fulfillment.
              </p>
            </div>

            {/* Table Search Input */}
            <div className="relative min-w-[260px]">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground-muted" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search category, supplier, code..."
                className="w-full h-9 pl-9 pr-3 text-xs font-medium rounded-xl border border-input bg-background text-foreground placeholder:text-foreground-muted focus:outline-none focus:ring-1 focus:ring-primary font-poppins"
              />
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-left text-xs font-poppins">
            <thead className="bg-muted/60 border-b border-border text-foreground-muted text-[11px] font-bold uppercase tracking-wider">
              <tr>
                <th className="py-3 px-4">Commodity / Category</th>
                <th className="py-3 px-4">Primary Supplier</th>
                <th className="py-3 px-4 text-right">Invoiced Spend (₹)</th>
                <th className="py-3 px-4 text-right">Budget (₹)</th>
                <th className="py-3 px-4 text-right">Savings (₹)</th>
                <th className="py-3 px-4 text-center">PPV Variance</th>
                <th className="py-3 px-4 text-center">OTIF %</th>
                <th className="py-3 px-4 text-center">Compliance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {filteredRecords.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-8 text-center text-xs text-foreground-muted">
                    No intelligence records matching your search query.
                  </td>
                </tr>
              ) : (
                filteredRecords.map((item, idx) => (
                  <tr key={idx} className="hover:bg-muted/30 transition-colors">
                    <td className="py-3 px-4">
                      <span className="font-bold text-foreground block text-xs">{item.category}</span>
                      <span className="text-[10px] text-foreground-muted font-mono">{item.commodityCode}</span>
                    </td>
                    <td className="py-3 px-4">
                      <span className="font-bold text-foreground block">{item.supplier}</span>
                      <span className="text-[10px] text-foreground-muted font-mono">
                        {item.vendorCode} • {item.plant}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right font-mono font-bold text-foreground">
                      {formatInr(item.spend)}
                    </td>
                    <td className="py-3 px-4 text-right font-mono text-foreground-muted">
                      {formatInr(item.budget)}
                    </td>
                    <td className={`py-3 px-4 text-right font-mono font-bold ${
                      item.savings >= 0 ? 'text-emerald-600' : 'text-rose-600'
                    }`}>
                      {formatInr(item.savings)}
                    </td>
                    <td className="py-3 px-4 text-center font-mono">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                        item.ppvStatus === 'FAVORABLE'
                          ? 'bg-emerald-500/10 text-emerald-600 border border-emerald-500/20'
                          : 'bg-rose-500/10 text-rose-600 border border-rose-500/20'
                      }`}>
                        {item.ppvPercent > 0 ? `+${item.ppvPercent}%` : `${item.ppvPercent}%`}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center font-mono font-bold text-foreground">
                      <span className={`px-2 py-0.5 rounded text-[10px] ${
                        item.otifPercent >= 95
                          ? 'bg-emerald-500/10 text-emerald-700'
                          : item.otifPercent >= 90
                          ? 'bg-blue-500/10 text-blue-700'
                          : 'bg-rose-500/10 text-rose-700'
                      }`}>
                        {item.otifPercent}%
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold ${
                        item.compliance === 'COMPLIANT'
                          ? 'bg-emerald-500/10 text-emerald-600 border border-emerald-500/20'
                          : item.compliance === 'CONTRACT_AUDIT'
                          ? 'bg-amber-500/10 text-amber-600 border border-amber-500/20'
                          : 'bg-rose-500/10 text-rose-600 border border-rose-500/20'
                      }`}>
                        {item.compliance.replace(/_/g, ' ')}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Generate Executive Report Modal (Mounted via createPortal) */}
      {isReportModalOpen &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/75 backdrop-blur-sm p-4 animate-in fade-in font-poppins"
            onClick={() => !isGeneratingReport && setIsReportModalOpen(false)}
          >
            <div
              className="w-full max-w-lg rounded-2xl border border-border bg-card p-6 shadow-2xl space-y-4 font-poppins animate-in zoom-in-95 my-auto text-foreground"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between border-b border-border/60 pb-3">
                <div className="flex items-center gap-2 text-primary">
                  <FileSpreadsheet className="h-5 w-5" />
                  <h3 className="text-base font-bold text-foreground font-poppins">
                    Generate Executive Intelligence Report
                  </h3>
                </div>
                <button
                  onClick={() => !isGeneratingReport && setIsReportModalOpen(false)}
                  className="rounded-lg p-1 hover:bg-muted text-foreground-muted cursor-pointer"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {reportSuccessMessage ? (
                <div className="p-6 text-center space-y-2 bg-emerald-500/10 border border-emerald-500/20 rounded-xl">
                  <CheckCircle2 className="h-8 w-8 text-emerald-600 mx-auto" />
                  <p className="font-bold text-emerald-700 text-sm">{reportSuccessMessage}</p>
                  <p className="text-xs text-foreground-muted">Downloading file to your local drive...</p>
                </div>
              ) : (
                <form onSubmit={handleSimulateReportGeneration} className="space-y-4 text-xs font-poppins">
                  <div>
                    <label className="block font-bold text-foreground mb-1.5">
                      Report Module & Scope *
                    </label>
                    <select
                      value={reportType}
                      onChange={(e) => setReportType(e.target.value)}
                      className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-semibold text-foreground focus:ring-2 focus:ring-primary font-poppins"
                    >
                      <option value="SPEND_PPV">Executive Spend & Purchase Price Variance (PPV) Audit</option>
                      <option value="VENDOR_OTIF">Supplier On-Time In-Full (OTIF) & Quality PPM Scorecard</option>
                      <option value="CONTRACT_MAVERICK">Maverick Off-Catalog Leakage & Tail Spend Analysis</option>
                      <option value="GST_ITC">Statutory GST & GSTR-2B Input Tax Credit Reconciliation</option>
                      <option value="CASH_FLOW">Accounts Payable Aging & Working Capital Cash Flow Forecast</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block font-bold text-foreground mb-1.5">
                        Fiscal Period *
                      </label>
                      <select
                        value={selectedPeriod}
                        onChange={(e) => setSelectedPeriod(e.target.value)}
                        className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-semibold text-foreground focus:ring-2 focus:ring-primary font-poppins"
                      >
                        <option value="FY_2025_26">Full Fiscal Year 2025-26</option>
                        <option value="Q4_2026">Q4 (Jan - Mar 2026)</option>
                        <option value="Q3_2025">Q3 (Oct - Dec 2025)</option>
                        <option value="YTD">Year-to-Date (YTD)</option>
                      </select>
                    </div>

                    <div>
                      <label className="block font-bold text-foreground mb-1.5">
                        Export File Format *
                      </label>
                      <select
                        value={reportFormat}
                        onChange={(e) => setReportFormat(e.target.value)}
                        className="w-full rounded-xl border border-input bg-background p-2.5 text-xs font-semibold text-foreground focus:ring-2 focus:ring-primary font-poppins"
                      >
                        <option value="PDF">Executive PDF Dossier</option>
                        <option value="EXCEL">Microsoft Excel (.xlsx)</option>
                        <option value="CSV">Raw Tabular CSV</option>
                      </select>
                    </div>
                  </div>

                  <div className="p-3 bg-muted/40 rounded-xl border border-border space-y-1 text-xs">
                    <span className="font-bold text-foreground block">Automated Regulatory Compliance:</span>
                    <p className="text-foreground-secondary text-[11px] leading-relaxed">
                      Generated report includes SAP S/4HANA transactional references, GSTIN compliance audit, and verified digital signatures.
                    </p>
                  </div>

                  <div className="flex items-center justify-end gap-3 pt-2 border-t border-border/60">
                    <Button
                      type="button"
                      variant="outline"
                      disabled={isGeneratingReport}
                      onClick={() => setIsReportModalOpen(false)}
                      className="cursor-pointer font-bold rounded-xl font-poppins"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={isGeneratingReport}
                      className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold font-poppins cursor-pointer px-5 shadow-xs"
                    >
                      {isGeneratingReport ? (
                        <>
                          <RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Compiling Data...
                        </>
                      ) : (
                        <>
                          <Download className="h-3.5 w-3.5 mr-1.5" /> Generate & Download
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};
